import { IDB } from "./IndexedDB/indexedDB";
import { ApiError, ErrorCode } from "./ApiError";
import { Action, VoidAction } from "../util/func";
import { z } from "zod";
import { putStatusResponseSchema, StatusResponse, statusResponseSchema } from "../schema/console/statusData";
import { GetCookieValue, SetCookie, DeleteCookie } from "../util/CookieManager";
import { tokenSchema } from "../schema/console/token";
import Time from "../util/Time";
import { validateConsumptionWrite } from "../util/consumption";
import { getFromIdPayload, getFromIdPayloadSchema, getFromIntIdPayload, getFromIntIdPayloadSchema, getFromMaterialIDPayload, getFromMaterialIdPayloadSchema, setDeletePayload, setDeletePayloadSchema } from "../schema/general";
import { issuancePutPayload, issuancePutPayloadSchema, issuanceRow, normalizedIssuanceResponseSchema } from "../schema/console/issuance";
import { hydrateIssuances } from "../util/hydrators/issuanceHydrator";
import { IssuanceBaseSchema, IssuanceBasePatchPayload } from "../schema/generated/Issuance";
import { LotBasePatchPayload, LotBasePutPayload, LotBaseSchema } from "../schema/generated/Lot";
import { lotRowBase, lotRowsBase, normalizedLotResponseSchema } from "../schema/console/lot";
import { hydrateLots } from "../util/hydrators/lotHydrator";
import { CarryOutBasePatchPayload, CarryOutBasePutPayload, CarryOutBaseSchema } from "../schema/generated/CarryOut";
import { CarryInBasePatchPayload, CarryInBasePutPayload, CarryInBaseSchema } from "../schema/generated/CarryIn";
import { carryInRow, carryInRows, normalizedCarryInResponseSchema } from "../schema/console/carry_in";
import { hydrateCarryIns } from "../util/hydrators/carryInHydrator";
import { qrScanProcessPayload, qrScanProcessSchema } from "../schema/console/qrScan";
import { carryOutRow, carryOutRows, normalizedCarryOutResponseSchema } from "../schema/console/carry_out";
import { hydrateCarryOuts } from "../util/hydrators/carryOutHydrator";
import { constructionPlanRow, constructionPlanRows, normalizedConstructionPlanResponseSchema } from "../schema/console/construction_plan";
import { hydrateConstructionPlans } from "../util/hydrators/constructionPlanHydrator";
import { ConstructionPlanBasePatchPayload, ConstructionPlanBasePutPayload, ConstructionPlanBaseSchema } from "../schema/generated/ConstructionPlan";
import { loginPayloadSchema } from "../schema/console/login";
import { LocalLoginUser, loginUserSchema, LoginUser } from "../schema/console/loginUser";
import { ConstructionPlanAreaBase } from "../schema/generated/ConstructionPlanArea";
import { PatchConstructionPlanAreaWithChildrenPayload, PutConstructionPlanAreaWithChildrenPayload, normalizedConstructionPlanAreaResponseSchema, patchConstructionPlanAreaWithChildrenSchema, putConstructionPlanAreaWithChildrenSchema } from "../schema/console/construction_plan_area";
import { constructionRow, constructionRows, normalizedConstructionResponseSchema } from "../schema/console/construction";
import { hydrateConstructions } from "../util/hydrators/constructionHydrator";
import { ConstructionBasePatchPayload, ConstructionBasePutPayload, ConstructionBaseSchema } from "../schema/generated/Construction";
import { ConstructionRecordBasePatchPayload, ConstructionRecordBasePutPayload, ConstructionRecordBaseSchema } from "../schema/generated/ConstructionRecord";
import { MaterialBaseSchema } from "../schema/generated/Material";
import { getMaterialFromIdSearchPayload, getMaterialFromIdSearchPayloadSchema, normalizedMaterialSearchResponseSchema, searchedMaterial, searchedMaterialRows } from "../schema/console/material";
import { hydrateMaterialSearch } from "../util/hydrators/materialSearchHydrator";
import { getUserListItemType, getUserType, normalizedUserResponseSchema, normalizedUserRowsResponseSchema } from "../schema/console/user";
import { hydrateUsers } from "../util/hydrators/userHydrator";
import { offlineResultBoundary, required } from "../util/typed";
import { WithOwner } from "./IndexedDB/IDBSchema";
import { EntityRowBounds, IdbRowPayload } from "./IndexedDB/IndexedDBApi";
import { getWorkCachePayload, GetWorkCachePayloadSchema, normalizedCacheableWorksResponseSchema, WorkCacheDataSchema, WorkPreviewRow } from "../schema/console/cache";
import { hydrateCacheableWorks } from "../util/hydrators/cacheableWorkHydrator";
import { entitySlugMap, idbEntityDefs, idbMergeDefs, EntityGetPath, EntityGetPlural, MergeRequestPath } from "./IndexedDB/generated/entityDefs";
import { MasterImportRequest, MasterImportResponse, masterImportRequestSchema, masterImportResponseSchema } from "../schema/console/master_import";

const ApiPath = process.env.REACT_APP_API_PATH;

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

// ---------------------------------------------------------------------------
// 新規エンティティ(マスタ/トランザクション)のCRUDエンドポイント情報。
// schema.json のメタデータ(consoleWrite: "registry" / mergeable / collectionKey)から
// 生成された entitySlugMap を単一情報源とする(旧・手書きリテラルはステージ1で撤去。
// 等価性は src/__tests__/api/generatedRegistry.test.ts が凍結値と照合して担保)。
// ---------------------------------------------------------------------------
export type { EntitySlugInfo } from "./IndexedDB/generated/entityDefs";

export const SLUG_MAP = entitySlugMap;

export type EntityWriteKey = keyof typeof SLUG_MAP;

// 全エンティティの可能なURL組み合わせを文字列型で表現する。
// 個別Pathに比べて緩いがゴミpathを通すリスクを実用上抑える。
type EntityWritePath =
  | `/put-${typeof SLUG_MAP[EntityWriteKey]["sing"]}`
  | `/patch-${typeof SLUG_MAP[EntityWriteKey]["sing"]}`
  | `/set-delete-${typeof SLUG_MAP[EntityWriteKey]["sing"]}`
  | `/merge-${typeof SLUG_MAP[EntityWriteKey]["plural"]}`;
type UserRequestPath = "/get-user-from-id" | "/get-users" | "/user-is-active";
type MaterialPath = "/get-material-from-id" | "/get-material-from-construction-id" | "/get-material-from-search-params";
type IssuancePath = "/get-issuance-from-id" | "/get-issuances" | "/put-issuance" | "/patch-issuance";
type CarryOutPath = "/get-carry-out-from-id" | "/get-carry-outs" | "/put-carry-out" | "/patch-carry-out" | "/set-delete-carry-out";
type LotPath = "/get-lot-from-id" | "/get-lots-from-carry-out-id" | "/put-lot" | "/patch-lot" | "/set-delete-lot" | "/process-lot";
type CarryInPath = "/get-carry-in-from-id" | "/get-carry-in-from-work-id" |  "/put-carry-in" | "/patch-carry-in" | "/set-delete-carry-in" | "/process-carry-in";
type ConstructionPlanPath =  "/get-construction-plan-from-id" | "/get-construction-plan-from-work-id" | "/put-construction-plan" | "/patch-construction-plan" | "/set-delete-construction-plan";
type ConstructionPlanAreaPath = "/get-construction-plan-area-from-id" | "/get-construction-plan-area-from-construction-plan-id" | "/put-construction-plan-area" | "/patch-construction-plan-area" | "/set-delete-construction-plan-area";
type ConstructionPath = "/get-construction-from-id" | "/get-construction-from-construction-plan-id" | "/put-construction" | "/patch-construction" | "/set-delete-construction" | "/material-scan-construction";
type ConstructionRecordPath = "/get-construction-record-from-construction-id" | "/put-construction-record" | "/patch-construction-record" | "/delete-construction-record";
type CachePath = "/get-cacheable-works" | "/get-cache-from-work-id";
export type NormalRequestPath =  UserRequestPath | IssuancePath | MaterialPath | CarryOutPath | LotPath | CarryInPath | ConstructionPlanPath | ConstructionPlanAreaPath | ConstructionPath | ConstructionRecordPath | CachePath;
type OnlineRequestPath = NormalRequestPath | MergeRequestPath | EntityWritePath | EntityGetPath | "/" | "/get-login-user" | "/login" | "/check-token" | "/logout" | "/import-masters";

if (!ApiPath) {
  throw new Error("API_PATHが設定されていません。 package.json、.env.xxを確認してください。");
}

// entities.putRow の応答スキーマを idType(整数PK/UUID PK)別に組み立てる。
function buildPutResSchema(idType: "int" | "uuid") {
  if (idType === "int") {
    return z.object({ status: z.string(), id: z.number() });
  }
  return z.object({ status: z.string(), id: z.string().uuid() });
}

// 親(工程実施/撹拌バッチ)の論理削除・復元に合わせて、その親を参照する消費行(施工材料消費)を1つの
// コホートとして扱い、deleted_at を書き換えた行を返す(オフライン用)。API 側 cascadeConsumptionCohort と
// 同じコホート方針: 削除方向は alive の子のみ、復元方向は「親と同時刻(previousDeletedAt)で削除された子」のみ。
// 復元は ms 完全一致を優先し、無い場合のみ秒 floor 一致へフォールバックする(サーバ再キャッシュで親 deleted_at が
// 秒精度へ丸められるため)。
function buildConsumptionCohortRows(
  targetRows: { deleted_at: number | null }[],
  isDeleting: boolean,
  previousDeletedAt: number | null,
  deletedAt: number | null,
  now: number,
): IdbRowPayload[] {
  if (isDeleting) {
    const aliveRows = targetRows.filter((row) => row.deleted_at == null);
    return aliveRows.map((row) => ({ ...row, deleted_at: deletedAt, updatedAt: now }));
  }
  if (previousDeletedAt == null) {
    return [];
  }
  const exactMatchRows = targetRows.filter((row) => row.deleted_at === previousDeletedAt);
  if (exactMatchRows.length > 0) {
    return exactMatchRows.map((row) => ({ ...row, deleted_at: deletedAt, updatedAt: now }));
  }
  const previousDeletedAtSecond = Math.floor(previousDeletedAt / 1000);
  const floorMatchRows = targetRows.filter((row) => row.deleted_at != null && Math.floor(row.deleted_at / 1000) === previousDeletedAtSecond);
  return floorMatchRows.map((row) => ({ ...row, deleted_at: deletedAt, updatedAt: now }));
}

export class GuestApi {
  async Login(id: string, password: string, showError: Action<string>) {
    try {
      const response = await GuestApi.onlineRequest(loginPayloadSchema, "POST", "/login", { login_id: id, password });
      const loginData = await GuestApi.GetResponseData(response, tokenSchema);
      
      // アプリ対応のためcookieはこちらで設定する
      // オフライン作業中に認証期限切れが起きると問題ありそうなのでクライアント側で認証期限の管理は行わない
      SetCookie("app-auth-token", loginData.token, { maxAge: 365 * Time.ADay });
      return true;
    } catch {
      showError("ログインに失敗しました。\nユーザーが存在しないか、ログイン情報が間違っています。");
      return false;
    }
  }

  protected static async onlineRequest<PayloadSchema extends z.ZodType>(payloadSchema: PayloadSchema, method: HttpMethod, path: OnlineRequestPath, data: z.infer<PayloadSchema>, tokenOverride?: string): Promise<Response> {
    const payloadJson = JSON.stringify(payloadSchema.parse(data));
    const headers: { [name: string]: string } = {};
    // アプリ用の認証対策としてCookieの代わりにヘッダにTokenを含める。
    // ユーザー切替機能で複数トークンを保持しているため、ログアウト等では対象トークンを明示できる
    const token = tokenOverride ?? GetCookieValue("app-auth-token");
    if (token) {
      headers["x-auth-token"] = token;
    }
    console.log(`requested ${path}`);
    if (method === "GET" || method === "DELETE") {
      // GET/DELETEはbodyを持てないためペイロードはクエリパラメータdataに載せる
      if (payloadJson === "{}") {
        return fetch(`${ApiPath}${path}`, { method, headers });
      } else {
        return fetch(`${ApiPath}${path}?data=${encodeURIComponent(payloadJson)}`, { method, headers });
      }
    } else {
      headers["Content-Type"] = "application/json";
      return fetch(`${ApiPath}${path}`, { method, headers, body: payloadJson });
    }
  }

  protected static requestEmpty(method: HttpMethod, path: OnlineRequestPath): Promise<Response> {
    return this.onlineRequest(z.object({}), method, path, {});
  }

  protected static async GetResponseData<ResSchema extends z.ZodType>(response: Response, resSchema: ResSchema): Promise<z.infer<ResSchema>> {
    const json = await response.json();
    if (response.ok) {
      return resSchema.parse(json);
    } else {
      throw new ApiError(response.status, json.error);
    }
  }

  static async CheckIsOnline(): Promise<boolean> {
    try {
      const statusResponse = await GuestApi.requestEmpty("GET", "/");
      const response = await GuestApi.GetResponseData(statusResponse, statusResponseSchema);
      return (response.status === "ok");
    } catch (e) {
      console.log(e);
      return false;
    }
  }
}

// 消費行のオフライン書き込み(put/patch/復元)ペイロードの形。
// put は id 無し、patch/復元は id 必須、それ以外の列は経路により部分的にのみ届く。
type ConsumptionWriteBody = {
  id?: string;
  construction_id?: string;
  material_qr_id?: number;
  stir_batch_id?: string | null;
  process_execution_id?: string | null;
  used_ratio?: number;
} & Record<string, unknown>;

export class AuthedApi extends GuestApi {
  static async Get(setNull: VoidAction, showError: Action<string>) {
    // IDB初期化&生成
    const idb = await new IDB(await IDB.initDB());

    // そもそも現状のままApiを使用できるかチェック
    let userToken: string | undefined;
    try {
      userToken = GetCookieValue("app-auth-token");
      if (!userToken) {
        const fallbackToken = await idb.loginUserDataApi.getAll().then(user => user.at(0)?.token);
        if (fallbackToken) {
          SetCookie("app-auth-token", fallbackToken, { maxAge: 365 * Time.ADay });
          userToken = fallbackToken;
        } else {
          throw new Error("有効なトークンがありません。");
        }
      }
    } catch (e) {
      if (userToken) {
        showError("ログイン失敗\n有効なトークンがありません。");
        DeleteCookie("app-auth-token");
      }
      return null;
    }

    // サーバーオンラインチェック
    const isOnline = await GuestApi.CheckIsOnline();

    // ユーザーログインチェック
    let loginUser: LocalLoginUser | undefined;
    try {
      if (isOnline) {
        const statusResponse = await GuestApi.requestEmpty("GET", "/check-token");
        await GuestApi.GetResponseData(statusResponse, statusResponseSchema);
        const loginDataResponse = await GuestApi.requestEmpty("GET", "/get-login-user");
        const loginData = await GuestApi.GetResponseData(loginDataResponse, loginUserSchema);
        loginUser = { ...loginData, token: userToken };
        await idb.loginUserDataApi.put(loginUser);
        console.log("ログインチェック通過");
      } else {
        loginUser = (await idb.loginUserDataApi.getAll()).find((localUser) => localUser.token === userToken);
      }
    } catch (e) {
      showError("ログイン失敗\nログイン処理中にエラーが発生しました。\n続けるには再度ログインしてください。")
      return null;
    }
    if (!loginUser) {
      showError("ログイン失敗\nユーザーを認証できません。ユーザーが存在しないか、ログイン情報が期限切れです。\n続けるには再度ログインしてください。")
      return null;
    }
    console.log(`ユーザー認証完了, ${isOnline ? "オンライン" : "オフライン"}モードで起動します。`);
    return new AuthedApi(idb, loginUser, isOnline, setNull, showError);
  }

  readonly indexedDB: IDB;
  readonly setNull;
  readonly showError;
  // ブラウザ実行のため NodeJS 名前空間に依存しない(実行環境の型定義差に左右されない形)
  readonly intervalId: ReturnType<typeof setInterval>;

  private _currentUser: LoginUser;
  public get currentUser(): LoginUser {
    return this._currentUser;
  }
  private set currentUser(state: LoginUser) {
    this._currentUser = state;
  }
  private _isOnline: boolean;
  private onlineStateListener = new Map<string, (isOnline: boolean) => void>();
  public subscribeOnlineState(id: string, cb: () => void): () => void {
    const listener: (isOnline: boolean) => void = () => cb();
    this.onlineStateListener.set(id, listener);
    return () => {
      const current = this.onlineStateListener.get(id);
      if (current === listener) {
        this.onlineStateListener.delete(id);
      }
    };
  };

  public get isOnline(): boolean {
    return this._isOnline;
  }
  private set isOnline(state: boolean) {
    this._isOnline = state;
    for (const l of this.onlineStateListener) l[1](this._isOnline)
  }
  
  public resetApi() {
    clearInterval(this.intervalId);
  }

  // MergeCacheData の多重実行ガード。1分を超える merge の最中に次の ServerCheckRoutine が
  // 起動しても、実行中と同じ Promise を返して重複送信(二重push)を防ぐ。
  private mergeCacheDataInFlight: Promise<boolean> | null = null;

  constructor (idb: IDB, loginUser: LocalLoginUser, isOnline: boolean, setNull: VoidAction, showError: Action<string>) {
    super();
    this.indexedDB = idb;
    this._isOnline = isOnline;
    this._currentUser = loginUser;
    this.showError = showError;
    this.setNull = setNull;

    if (this.isOnline) {
      // インスタンス作成と同時にn分毎にcheckServerConnectionでApiとの疎通確認を行うルーチンを起動する。
      // 失敗時は isOnline=false に落とし、毎分の ServerCheckRoutine に再試行させる
      // (fire-and-forget のままだと isOnline=true に取り残され、dirty行が残留し続ける)。
      this.MergeCacheData().then((merged) => {
        if (!merged) {
          this.isOnline = false;
        }
      });
    }
    this.intervalId = setInterval(() => {
      this.ServerCheckRoutine();
    }, 1 * Time.AMinute);
  }

  async SwitchUser(userId: string) {
    const targetUser = await this.login_user.get(userId);
    SetCookie("app-auth-token", targetUser.token, { maxAge: 365 * Time.ADay });
  }

  async Logout(userId: string) {
    // サーバ側のトークンも失効させる。オフライン時等の失敗はローカルのログアウトを妨げない
    const targetUser = await this.login_user.get(userId).catch(() => undefined);
    if (targetUser?.token) {
      try {
        await AuthedApi.onlineRequest(z.object({}), "POST", "/logout", {}, targetUser.token);
      } catch {
        console.log("サーバ側のトークン失効に失敗しました。");
      }
    }
    await this.indexedDB.loginUserDataApi.Delete(userId);
    if (userId === this.currentUser?.id) {
      DeleteCookie("app-auth-token");
      this.setNull();
    }
  }

  async ServerCheckRoutine() {
    try {
      const statusResponse = await GuestApi.requestEmpty("GET", "/");
      const response = await GuestApi.GetResponseData(statusResponse, statusResponseSchema);
      const reachable = response.status === "ok";
      if (!this.isOnline && reachable) {
        // とりあえずローカルDBのデータ全部送信、サーバー側でcacheDataとUpdatedAtを比較
        const merged = await this.MergeCacheData();

        // マージ失敗時はオフラインに留まり、次回のルーチンで再試行する
        if (merged) {
          this.isOnline = reachable;
        }
      }
    } catch (e) {
      // エラーならオフラインに違いない
      this.isOnline = false;
    }
  }

  // 実行中のマージに割り込む形で MergeCacheData が要求されたとき、完了後にもう1周実行するためのフラグ。
  // 実行中サイクルの uploadTime 以降に書かれた dirty 行(オフライン復帰の直前編集など)は実行中サイクルでは
  // 送信されないため、要求を握りつぶすと isOnline=true のまま dirty 行が残留し、次のオフライン往復まで
  // push されない(毎分の ServerCheckRoutine はオフライン時しかマージしない)。
  private mergeRerunRequested = false;

  // オフライン編集されたデータをテーブルの依存順(親→子)にサーバーへ送信する
  // 戻り値は全エンティティのマージが成功したかどうか
  async MergeCacheData(): Promise<boolean> {
    if (this.mergeCacheDataInFlight) {
      // 実行中サイクルに相乗りせず、完了後の再実行を予約する(この呼び出しの時点までの編集を確実に押し出す)。
      this.mergeRerunRequested = true;
      return this.mergeCacheDataInFlight;
    }
    this.mergeCacheDataInFlight = (async () => {
      let merged = await this.runMergeCacheData();
      // 再実行は上限付き。要求が実行中に連続して積まれ続けても際限なく回さない
      // (取りこぼした dirty 行は次のオフライン→オンライン遷移か手動マージで回収される)。
      let rerunCount = 0;
      while (this.mergeRerunRequested && rerunCount < 3) {
        this.mergeRerunRequested = false;
        rerunCount += 1;
        merged = await this.runMergeCacheData();
      }
      this.mergeRerunRequested = false;
      return merged;
    })().finally(() => {
      this.mergeCacheDataInFlight = null;
    });
    return this.mergeCacheDataInFlight ?? false;
  }

  private async runMergeCacheData(): Promise<boolean> {
    try {
      // 中途半端なデータを送信しないため、マージ開始以降のデータは次の機会に見送り。
      const uploadTime = Time.Now().GetTime();
      // ユーザー切替(Cookieトークン変更)を検知するための開始時点のトークン。
      // 切替中に旧ユーザーの行を新トークンで push してしまうのを防ぐため、エンティティごとに比較する。
      const mergeStartToken = GetCookieValue("app-auth-token");

      for (const mergeDef of idbMergeDefs) {
        if (GetCookieValue("app-auth-token") !== mergeStartToken) {
          console.warn("マージ中にユーザーが切り替わったため、マージを中断しました。");
          return false;
        }
        const entityApi = this.indexedDB.GetEntityApi(mergeDef.store);
        const cache = await entityApi.getCache();
        // 他ログインユーザーの行(切替機能で複数ユーザーがローカルに残っている場合)を
        // 現トークンのユーザーとして push しないよう、所有者を現在ユーザーに限定する。
        const dirtyRows = cache.filter(d => d.ownerId === this.currentUser.id && (d.updatedAt ?? 0) >= d.cachedAt && (d.updatedAt ?? 0) < uploadTime);
        try {
          // 開始時点のトークンを明示的に指定する。中断ガード通過後〜fetch実行までの間に
          // ユーザーが切り替わっても、送信自体は必ず開始時ユーザーのトークンで行われるようにする。
          const response = await AuthedApi.onlineRequest(mergeDef.payloadSchema, "POST", mergeDef.path, dirtyRows, mergeStartToken);
          await AuthedApi.GetResponseData(response, statusResponseSchema);
          // push成功行の cachedAt を更新し dirty を解消する。削除優先 で敗れた行や
          // ユニーク吸収された行も、次回の全件スナップショット reconcile で正しく トゥームストーン 化される。
          await entityApi.markPushed(dirtyRows.map(d => ({ id: d.id, ownerId: d.ownerId })), uploadTime);
        } catch (e) {
          // どのエンティティ・何件で失敗したかを残す。ここが失敗し続けるとオンライン復帰できない
          // (毒データ1行で永久オフライン)ため、原因調査に必須の情報。
          console.error(`マージに失敗しました: ${mergeDef.path} (${dirtyRows.length}件)`, e);
          throw e;
        }
      }

      console.log("マージまでうまくいきました。");
      await this.indexedDB.RemoveInActiveCache();
      console.log("不要なデータを削除しました。");
      return true;
    } catch {
      console.error("マージに失敗しました。");
      return false;
    }
  }

  protected async request<PayloadSchema extends z.AnyZodObject, ResSchema extends z.ZodType>(payloadSchema: PayloadSchema, resSchema: ResSchema, method: HttpMethod, path: NormalRequestPath, data: z.infer<PayloadSchema>, topKeyName: string, offlineApi?: (args: WithOwner<z.infer<PayloadSchema>>) => Promise<z.infer<ResSchema> | null>, noErrorHandle?: boolean): Promise<z.infer<ResSchema>> {
    if (this.isOnline) {
      let response = null;
      try {
        response = await AuthedApi.onlineRequest(payloadSchema, method, path, data);
      } catch(e) {
        this.isOnline = false;
      }
      
      if (response) {
        if (response.ok && 200 <= response.status && response.status < 300) {
          const result = await AuthedApi.GetResponseData(response, resSchema);
        
          // 正常に取得できた場合のキャッシュ処理を行う
          await this.indexedDB.SetCache(result, this.currentUser.id, topKeyName);
          return result;
        } else if (!noErrorHandle) {
          if (500 <= response.status) {
            // サーバーに到達出来ない場合はOfflineとして状態を更新する
            this.isOnline = false;
          } else {
            const json = await response.json();
            if (!noErrorHandle && (json.code === ErrorCode.InvalidApiAuthToken || json.code === ErrorCode.ApiAuthTokenNotFound || json.code === ErrorCode.InvalidToken)) {
              this.showError(`認証エラー：${json.error}\n前回ログインから時間が経ち過ぎているか、ユーザーが削除されたためオンライン状態を維持できませんでした。\n現在の作業を保存するため一時的にオフラインモードに移行しています。\n作業終了後再度ログインしてください。`);
              this.isOnline = false;
            } else {
              // 認証エラー以外の 4xx はバリデーション失敗等の業務エラーであり、
              // オンライン接続自体は生きているため isOnline は変更しない(writeEntityRow と同じ扱い)。
              this.showError(`リクエストエラー\n${json.error}`);
              throw new ApiError(json.code, json.error);
            }
          }
        }
      }
    } 

    // 上手くいかなかったらIndexedDBで処理
    if (offlineApi) {
      try {
        console.log(`request: ${path}`);
        const result = await offlineApi({ ...data, ownerId: this.currentUser.id });
        console.log("----------offline request result----------")
        console.log(result);
        console.log("----------offline request result----------")
        return result;
      } catch(error) {
        this.showError(`内部エラー\n${error}`);
        throw new ApiError(0, `${error}`);
      }
    }

    throw new Error("リクエストを処理できませんでした。");
  }

  // 正規化応答メソッド共通の「オンライン試行 → オフラインフォールバック」の定型。
  // オンライン時は online(request で正規化応答を受信し、必要ならハイドレータで従来のネスト形へ復元)を試す。
  // 4xx の業務エラー(ApiError。対象未存在・バリデーション失敗等)は従来どおり呼び出し元へ伝え、
  // それ以外の失敗(接続要因。request 内で isOnline=false 化)はオフライン経路(IndexedDB DataApi)で継続する。
  // request() が正規化応答を SetCache するため、関連エンティティのローカルキャッシュも従来より充実する。
  // オフライン結果に行が無い場合、応答宣言型のまま実行時 null が返る従来挙動は
  // offlineResultBoundary(説明は util/typed.ts)がここ1箇所で吸収する。
  // null 許容戻りのメソッド(issuance.getFromId 等)は T へ null を含めて同一に扱う。
  private async withOfflineFallback<T>(online: () => Promise<T>, offline: () => Promise<T | null>): Promise<T> {
    if (this.isOnline) {
      try {
        return await online();
      } catch (e) {
        if (e instanceof ApiError) {
          throw e;
        }
        // ApiError 以外(接続要因)は下のオフライン経路で継続する
      }
    }
    return offlineResultBoundary(await offline());
  }

  public readonly user_data = {
    // オンラインは正規化応答(Users + related.Groups)を共通ハイドレータで Group 込みの従来形へ復元する
    getFromId: (payload: getFromIdPayload): Promise<getUserType> => this.withOfflineFallback(
      async () => required(hydrateUsers(await this.request(getFromIdPayloadSchema, normalizedUserResponseSchema, "GET", "/get-user-from-id", payload, "Users"))[0], `利用者(${payload.id})の正規化応答の行`),
      () => this.indexedDB.userDataApi.getFullDataById({ ...payload, ownerId: this.currentUser.id }),
    ),
    // 一覧はオンライン専用(オフラインフォールバックなし)。login_id 付きの行を返す。
    get: async (): Promise<getUserListItemType[]> => {
      const normalized = await this.request(z.object({}), normalizedUserRowsResponseSchema, "GET", "/get-users", {}, "Users");
      return hydrateUsers(normalized);
    },
    getIsActive: (payload: getFromIdPayload) => this.request(getFromIdPayloadSchema, z.object({ isActive: z.boolean() }), "GET", "/user-is-active", payload, "", undefined, true)
  }

  public readonly cache = {
    // オンライン専用(オフラインフォールバックなし)。正規化応答(Works の軽量プレビュー行 + 空の related)を
    // 受けて一覧行へ変換する。工事(Work)は担当者を持たないため related 結合は不要。
    getCacheAbleWorks: async (): Promise<WorkPreviewRow[]> => {
      const normalized = await this.request(z.object({}), normalizedCacheableWorksResponseSchema, "GET", "/get-cacheable-works", {}, "Works");
      return hydrateCacheableWorks(normalized);
    },
    // 応答は全バケット平坦の一括キャッシュ束。request() の SetCache が各バケットを IndexedDB へ書き込む。
    getCacheFromWorkIds: (payload: getWorkCachePayload) => this.request(GetWorkCachePayloadSchema, WorkCacheDataSchema, "POST", "/get-cache-from-work-id", payload, "")
  };

  public readonly login_user = {
    getAll: () => this.indexedDB.loginUserDataApi.getAll(),
    get: (id: string) => this.indexedDB.loginUserDataApi.get(id)
  }

  public readonly issuance = {
    // オンラインは正規化応答(Issuances + related バケット)を共通ハイドレータでネスト形へ復元する
    getFromId: (payload: getFromIdPayload): Promise<issuanceRow | null> => this.withOfflineFallback(
      async () => hydrateIssuances(await this.request(getFromIdPayloadSchema, normalizedIssuanceResponseSchema, "GET", "/get-issuance-from-id", payload, "Issuances"))[0] ?? null,
      () => this.indexedDB.issuanceDataApi.getRowById({ ...payload, ownerId: this.currentUser.id }),
    ),
    get: (): Promise<issuanceRow[]> => this.withOfflineFallback(
      async () => hydrateIssuances(await this.request(z.object({}), normalizedIssuanceResponseSchema, "GET", "/get-issuances", {}, "Issuances")),
      () => this.indexedDB.issuanceDataApi.getRowsById({ ownerId: this.currentUser.id }),
    ),
    put: (payload: issuancePutPayload) => this.request(issuancePutPayloadSchema, putStatusResponseSchema, "PUT", "/put-issuance", payload, ""),
    patch: (payload: IssuanceBasePatchPayload) => this.request(IssuanceBaseSchema.patch, statusResponseSchema, "PATCH", "/patch-issuance", payload, "", this.indexedDB.issuanceDataApi.patch.bind(this.indexedDB.issuanceDataApi)),
  }

  public readonly material = {
    // QR詳細も検索と同一機構: 正規化応答(重複排除)をハイドレータでネスト形へ復元する
    getLifeSpanFromId: (payload: getFromMaterialIDPayload): Promise<searchedMaterial | null> => this.withOfflineFallback(
      async () => hydrateMaterialSearch(await this.request(getFromMaterialIdPayloadSchema, normalizedMaterialSearchResponseSchema, "GET", "/get-material-from-id", payload, "Materials"))[0] ?? null,
      () => this.indexedDB.materialDataApi.getLifeSpanFromId({ ...payload, ownerId: this.currentUser.id }),
    ),
    getFromConstructionId: (payload: getFromIdPayload) => this.request(getFromIdPayloadSchema, MaterialBaseSchema.rows, "GET", "/get-material-from-construction-id", payload, "Material", this.indexedDB.materialDataApi.getByConstructionId.bind(this.indexedDB.materialDataApi)),
    // オフラインで行が無い場合は空配列を返す(null 吸収ではなく [] が従来挙動)
    getFromIdSearch: (payload: getMaterialFromIdSearchPayload): Promise<searchedMaterialRows> => this.withOfflineFallback(
      async () => hydrateMaterialSearch(await this.request(getMaterialFromIdSearchPayloadSchema, normalizedMaterialSearchResponseSchema, "POST", "/get-material-from-search-params", payload, "Materials")),
      async () => await this.indexedDB.materialDataApi.getLifeSpanFromIds({ ...payload, ownerId: this.currentUser.id }) ?? [],
    )
  }

  public readonly carry_out = {
    // オンラインは正規化応答(CarryOuts + related バケット)を共通ハイドレータでネスト形へ復元する
    getFromId: (payload: getFromIdPayload): Promise<carryOutRow> => this.withOfflineFallback(
      async () => required(hydrateCarryOuts(await this.request(getFromIdPayloadSchema, normalizedCarryOutResponseSchema, "GET", "/get-carry-out-from-id", payload, "CarryOuts"))[0], `出荷(${payload.id})の正規化応答の行`),
      () => this.indexedDB.carryOutDataApi.getRowById({ ...payload, ownerId: this.currentUser.id }),
    ),
    get: (): Promise<carryOutRows> => this.withOfflineFallback(
      async () => hydrateCarryOuts(await this.request(z.object({}), normalizedCarryOutResponseSchema, "GET", "/get-carry-outs", {}, "CarryOuts")),
      () => this.indexedDB.carryOutDataApi.getRowsById({ ownerId: this.currentUser.id }),
    ),
    put: (payload: CarryOutBasePutPayload) => this.request(CarryOutBaseSchema.put, putStatusResponseSchema, "PUT", "/put-carry-out", payload, "", this.indexedDB.carryOutDataApi.put.bind(this.indexedDB.carryOutDataApi)),
    patch: (payload: CarryOutBasePatchPayload) => this.request(CarryOutBaseSchema.patch, statusResponseSchema, "PATCH", "/patch-carry-out", payload, "", this.indexedDB.carryOutDataApi.patch.bind(this.indexedDB.carryOutDataApi)),
    setDelete: (payload: setDeletePayload) => this.request(setDeletePayloadSchema, statusResponseSchema, "DELETE", "/set-delete-carry-out", payload, "", this.indexedDB.carryOutDataApi.delete.bind(this.indexedDB.carryOutDataApi)),
  }

  public readonly lot = {
    // オンラインは正規化応答(Lots + related バケット)を共通ハイドレータでネスト形へ復元する
    getFromId: (payload: getFromIdPayload): Promise<lotRowBase> => this.withOfflineFallback(
      async () => required(hydrateLots(await this.request(getFromIdPayloadSchema, normalizedLotResponseSchema, "GET", "/get-lot-from-id", payload, "Lots"))[0], `ロット(${payload.id})の正規化応答の行`),
      () => this.indexedDB.lotDataApi.getRowById({ ...payload, ownerId: this.currentUser.id }),
    ),
    getFromCarryOutId: (payload: getFromIdPayload): Promise<lotRowsBase> => this.withOfflineFallback(
      async () => hydrateLots(await this.request(getFromIdPayloadSchema, normalizedLotResponseSchema, "GET", "/get-lots-from-carry-out-id", payload, "Lots")),
      () => this.indexedDB.lotDataApi.getRowsByCarryOutId({ ...payload, ownerId: this.currentUser.id }),
    ),
    put: (payload: LotBasePutPayload) => this.request(LotBaseSchema.put, putStatusResponseSchema, "PUT", "/put-lot", payload, "", this.indexedDB.lotDataApi.put.bind(this.indexedDB.lotDataApi)),
    patch: (payload: LotBasePatchPayload) => this.request(LotBaseSchema.patch, statusResponseSchema, "PATCH", "/patch-lot", payload, "", this.indexedDB.lotDataApi.patch.bind(this.indexedDB.lotDataApi)),
    setDelete: (payload: setDeletePayload) => this.request(setDeletePayloadSchema, statusResponseSchema, "DELETE", "/set-delete-lot", payload, "", this.indexedDB.lotDataApi.delete.bind(this.indexedDB.lotDataApi)),
    process: (payload: qrScanProcessPayload) => this.request(qrScanProcessSchema, statusResponseSchema, "POST", "/process-lot", payload, "", this.indexedDB.lotDataApi.process.bind(this.indexedDB.lotDataApi))
  }

  public readonly carry_in = {
    // オンラインは正規化応答(CarryIns + related バケット)を共通ハイドレータでネスト形へ復元する
    getFromId: (payload: getFromIdPayload): Promise<carryInRow> => this.withOfflineFallback(
      async () => required(hydrateCarryIns(await this.request(getFromIdPayloadSchema, normalizedCarryInResponseSchema, "GET", "/get-carry-in-from-id", payload, "CarryIns"))[0], `搬入(${payload.id})の正規化応答の行`),
      () => this.indexedDB.carryInDataApi.getRowById({ ...payload, ownerId: this.currentUser.id }),
    ),
    // 工事(Work, id=number)配下の搬入を取得する。
    getFromWorkId: (payload: getFromIntIdPayload): Promise<carryInRows> => this.withOfflineFallback(
      async () => hydrateCarryIns(await this.request(getFromIntIdPayloadSchema, normalizedCarryInResponseSchema, "GET", "/get-carry-in-from-work-id", payload, "CarryIns")),
      () => this.indexedDB.carryInDataApi.getRowsByWorkId({ ...payload, ownerId: this.currentUser.id }),
    ),
    put: (payload: CarryInBasePutPayload) => this.request(CarryInBaseSchema.put, putStatusResponseSchema, "PUT", "/put-carry-in", payload, "", this.indexedDB.carryInDataApi.put.bind(this.indexedDB.carryInDataApi)),
    patch: (payload: CarryInBasePatchPayload) => this.request(CarryInBaseSchema.patch, statusResponseSchema, "PATCH", "/patch-carry-in", payload, "", this.indexedDB.carryInDataApi.patch.bind(this.indexedDB.carryInDataApi)),
    setDelete: (payload: setDeletePayload) => this.request(setDeletePayloadSchema, statusResponseSchema, "DELETE", "/set-delete-carry-in", payload, "", this.indexedDB.carryInDataApi.delete.bind(this.indexedDB.carryInDataApi)),
    process: (payload: qrScanProcessPayload) => this.request(qrScanProcessSchema, statusResponseSchema, "POST", "/process-carry-in", payload, "", this.indexedDB.carryInDataApi.process.bind(this.indexedDB.carryInDataApi))
  }

  public readonly construction_plan = {
    // オンラインは正規化応答(ConstructionPlans + related バケット)を共通ハイドレータでネスト形へ復元する
    getFromId: (payload: getFromIdPayload): Promise<constructionPlanRow> => this.withOfflineFallback(
      async () => required(hydrateConstructionPlans(await this.request(getFromIdPayloadSchema, normalizedConstructionPlanResponseSchema, "GET", "/get-construction-plan-from-id", payload, "ConstructionPlans"))[0], `施工計画(${payload.id})の正規化応答の行`),
      () => this.indexedDB.constructionPlanDataApi.getRowById({ ...payload, ownerId: this.currentUser.id }),
    ),
    // 工事(Work, id=number)配下の施工計画を取得する。
    getFromWorkId: (payload: getFromIntIdPayload): Promise<constructionPlanRows> => this.withOfflineFallback(
      async () => hydrateConstructionPlans(await this.request(getFromIntIdPayloadSchema, normalizedConstructionPlanResponseSchema, "GET", "/get-construction-plan-from-work-id", payload, "ConstructionPlans")),
      () => this.indexedDB.constructionPlanDataApi.getRowsByWorkId({ ...payload, ownerId: this.currentUser.id }),
    ),
    put: (payload: ConstructionPlanBasePutPayload) => this.request(ConstructionPlanBaseSchema.put, putStatusResponseSchema, "PUT", "/put-construction-plan", payload, "", this.indexedDB.constructionPlanDataApi.put.bind(this.indexedDB.constructionPlanDataApi)),
    patch: (payload: ConstructionPlanBasePatchPayload) => this.request(ConstructionPlanBaseSchema.patch, statusResponseSchema, "PATCH", "/patch-construction-plan", payload, "", this.indexedDB.constructionPlanDataApi.patch.bind(this.indexedDB.constructionPlanDataApi)),
    setDelete: (payload: setDeletePayload) => this.request(setDeletePayloadSchema, statusResponseSchema, "DELETE", "/set-delete-construction-plan", payload, "", this.indexedDB.constructionPlanDataApi.delete.bind(this.indexedDB.constructionPlanDataApi)),
  }

  public readonly construction_plan_area = {
    // オンラインは正規化応答(ConstructionPlanAreas + related バケット)を受ける。従来もクライアント契約は
    // 平坦な施工箇所行のみ(子は zod strip で捨てていた)のため、ハイドレータ不要でトップ行を返す。
    getFromId: (payload: getFromIdPayload): Promise<ConstructionPlanAreaBase> => this.withOfflineFallback(
      async () => required((await this.request(getFromIdPayloadSchema, normalizedConstructionPlanAreaResponseSchema, "GET", "/get-construction-plan-area-from-id", payload, "ConstructionPlanAreas")).ConstructionPlanAreas[0], `施工箇所(${payload.id})の正規化応答の行`),
      () => this.indexedDB.constructionPlanAreaDataApi.getById({ ...payload, ownerId: this.currentUser.id }),
    ),
    getFromConstructionPlanId: (payload: getFromIdPayload): Promise<ConstructionPlanAreaBase[]> => this.withOfflineFallback(
      async () => (await this.request(getFromIdPayloadSchema, normalizedConstructionPlanAreaResponseSchema, "GET", "/get-construction-plan-area-from-construction-plan-id", payload, "ConstructionPlanAreas")).ConstructionPlanAreas,
      () => this.indexedDB.constructionPlanAreaDataApi.getByConstructionPlanId({ ...payload, ownerId: this.currentUser.id }),
    ),
    // 子込み(locations / planned_materials)はサーバの専用トランザクション route(delete-insert方式)で親子一括保存される。
    // 親(ConstructionPlanArea)と子2テーブル(ConstructionPlanAreaLocation / ConstructionPlanAreaPlannedMaterial)は
    // いずれも hasMerge:true のため、オフライン時・送信失敗時は constructionPlanAreaDataApi.put/patch へフォールバックし、
    // IDB上で同じdelete-insert意味論を再現してdirty行を積む(オンライン復帰時のmerge pushで反映される)。
    put: (payload: PutConstructionPlanAreaWithChildrenPayload) => this.request(putConstructionPlanAreaWithChildrenSchema, putStatusResponseSchema, "PUT", "/put-construction-plan-area", payload, "", this.indexedDB.constructionPlanAreaDataApi.put.bind(this.indexedDB.constructionPlanAreaDataApi)),
    patch: (payload: PatchConstructionPlanAreaWithChildrenPayload) => this.request(patchConstructionPlanAreaWithChildrenSchema, statusResponseSchema, "PATCH", "/patch-construction-plan-area", payload, "", this.indexedDB.constructionPlanAreaDataApi.patch.bind(this.indexedDB.constructionPlanAreaDataApi)),
    setDelete: (payload: setDeletePayload) => this.request(setDeletePayloadSchema, statusResponseSchema, "DELETE", "/set-delete-construction-plan-area", payload, "", this.indexedDB.constructionPlanAreaDataApi.delete.bind(this.indexedDB.constructionPlanAreaDataApi)),
  }

  public readonly construction = {
    // オンラインは正規化応答(Constructions + related バケット)を共通ハイドレータでネスト形へ復元する。
    // Materials の施工×材料QR重複排除もハイドレータ側で行う(旧 api attachConsumedMaterials と同一規則)。
    getFromId: (payload: getFromIdPayload): Promise<constructionRow> => this.withOfflineFallback(
      async () => required(hydrateConstructions(await this.request(getFromIdPayloadSchema, normalizedConstructionResponseSchema, "GET", "/get-construction-from-id", payload, "Constructions"))[0], `施工(${payload.id})の正規化応答の行`),
      () => this.indexedDB.constructionDataApi.getRowById({ ...payload, ownerId: this.currentUser.id }),
    ),
    getFromConstructionPlanId: (payload: getFromIdPayload): Promise<constructionRows> => this.withOfflineFallback(
      async () => hydrateConstructions(await this.request(getFromIdPayloadSchema, normalizedConstructionResponseSchema, "GET", "/get-construction-from-construction-plan-id", payload, "Constructions")),
      () => this.indexedDB.constructionDataApi.getRowsByConstructionPlanId({ ...payload, ownerId: this.currentUser.id }),
    ),
    put: (payload: ConstructionBasePutPayload) => this.request(ConstructionBaseSchema.put, putStatusResponseSchema, "PUT", "/put-construction", payload, "", this.indexedDB.constructionDataApi.put.bind(this.indexedDB.constructionDataApi)),
    patch: (payload: ConstructionBasePatchPayload) => this.request(ConstructionBaseSchema.patch, statusResponseSchema, "PATCH", "/patch-construction", payload, "", this.indexedDB.constructionDataApi.patch.bind(this.indexedDB.constructionDataApi)),
    materialScan: (payload: qrScanProcessPayload) => this.request(qrScanProcessSchema, statusResponseSchema, "POST", "/material-scan-construction", payload, "", this.indexedDB.constructionDataApi.process.bind(this.indexedDB.constructionDataApi)),
    setDelete: (payload: setDeletePayload) => this.request(setDeletePayloadSchema, statusResponseSchema, "DELETE", "/set-delete-construction", payload, "", this.indexedDB.constructionDataApi.delete.bind(this.indexedDB.constructionDataApi))
  }

  public readonly construction_record = {
    getFromConstructionId: (payload: getFromIdPayload) => this.request(getFromIdPayloadSchema, ConstructionRecordBaseSchema.rows, "GET", "/get-construction-record-from-construction-id", payload, "ConstructionRecords", this.indexedDB.constructionRecordDataApi.getByConstructionId.bind(this.indexedDB.constructionRecordDataApi)),
    put: (payload: ConstructionRecordBasePutPayload) => this.request(ConstructionRecordBaseSchema.put, putStatusResponseSchema, "PUT", "/put-construction-record", payload, "", this.indexedDB.constructionRecordDataApi.put.bind(this.indexedDB.constructionRecordDataApi)),
    patch: (payload: ConstructionRecordBasePatchPayload) => this.request(ConstructionRecordBaseSchema.patch, statusResponseSchema, "PATCH", "/patch-construction-record", payload, "", this.indexedDB.constructionRecordDataApi.patch.bind(this.indexedDB.constructionRecordDataApi)),
    setDelete: (payload: setDeletePayload) => this.request(setDeletePayloadSchema, statusResponseSchema, "DELETE", "/delete-construction-record", payload, "", this.indexedDB.constructionRecordDataApi.delete.bind(this.indexedDB.constructionRecordDataApi))
  }

  // name(EntityWriteKey)に対応するIDBストア名を idbMergeDefs から引く。
  // hasMerge:true のエンティティのみ merge定義を持つため、見つからなければ呼び出し側の誤用。
  private offlineStoreFor(name: EntityWriteKey): string {
    const info = SLUG_MAP[name];
    const mergeDef = idbMergeDefs.find((d) => d.path === `/merge-${info.plural}`);
    if (mergeDef == null) {
      throw new Error(`${String(name)} はオフライン書込み対応(merge対応)ではありません。`);
    }
    return mergeDef.store;
  }

  // 施工材料消費のオフライン書き込み(put/patch/復元)の事前検証と正規化。API と同じ意味論
  // (親の生存・所属整合・Σ used_ratio ≤ 1、および工程実施のバッチ由来の自動導出)をローカル
  // IndexedDB のデータで再現し、違反は書き込み前にエラーにする(復帰マージでサーバに拒否される
  // 書き込みや Σ 超過の座礁をローカルで防ぐ)。戻り値は書き込みに使う正規化済み body
  // (put / バッチ実変更 patch で process_execution_id 未指定の場合、IndexedDB のバッチ行から導出して
  // 補完する = API 側 resolveProcessExecutionId と同じ規則)。
  // patch は API と同じく、親参照が実変更される場合のみ親検証を行う(同値再送・used_ratio のみの修正は
  // Σ 検証のみ)。merge(復帰処理そのもの)はこの検証を通らない。
  private async prepareOfflineConsumptionWrite(kind: "put" | "patch" | "restore", body: ConsumptionWriteBody): Promise<ConsumptionWriteBody> {
    const ownerId = this.currentUser.id;
    const consumptions = await this.indexedDB.generated.constructionMaterialConsumption.getAllByOwner(ownerId, false);
    const stirBatches = await this.indexedDB.generated.stirBatch.getAllByOwner(ownerId, false);
    const deriveProcessExecutionId = (stirBatchId: string | null): string | null => {
      if (stirBatchId == null) {
        return null;
      }
      const stirBatch = stirBatches.find((row) => row.id === stirBatchId);
      return stirBatch?.process_execution_id ?? null;
    };

    let guardedBody = body;
    let nextRow: { construction_id: string; material_qr_id: number; stir_batch_id: string | null; process_execution_id: string | null; used_ratio: number; excludeConsumptionId: string | null } = { construction_id: "", material_qr_id: 0, stir_batch_id: null, process_execution_id: null, used_ratio: 1, excludeConsumptionId: null };
    let checkParents = true;
    if (kind === "put") {
      // API の put(resolveProcessExecutionId)と同じく、工程実施が未指定(undefined と明示 null の両方)
      // ならバッチから導出して保存する。
      const putProcessExecutionId = body.process_execution_id != null ? body.process_execution_id : deriveProcessExecutionId(body.stir_batch_id ?? null);
      guardedBody = { ...body, process_execution_id: putProcessExecutionId };
      nextRow = {
        // put payload に必須の列だが型上は optional のため、欠落時は検証が拒否する値で埋める
        construction_id: body.construction_id ?? "",
        material_qr_id: body.material_qr_id ?? 0,
        stir_batch_id: body.stir_batch_id ?? null,
        process_execution_id: putProcessExecutionId,
        used_ratio: body.used_ratio ?? 1,
        excludeConsumptionId: null,
      };
    } else {
      const existing = consumptions.find((row) => row.id === body.id);
      if (existing == null) {
        // 対象行が無い場合の失敗は後段の offlinePatch / offlineSetDelete の既存エラーに委ねる。
        return body;
      }
      const nextConstructionId = body.construction_id !== undefined ? body.construction_id : existing.construction_id;
      const nextStirBatchId = body.stir_batch_id !== undefined ? body.stir_batch_id : existing.stir_batch_id;
      let nextProcessExecutionId = body.process_execution_id !== undefined ? body.process_execution_id : existing.process_execution_id;
      if (kind === "patch" && body.stir_batch_id !== undefined && nextStirBatchId !== existing.stir_batch_id && body.process_execution_id === undefined) {
        // API の patch と同じく、バッチが実変更され工程実施が未指定なら新バッチから再導出して保存する。
        nextProcessExecutionId = deriveProcessExecutionId(nextStirBatchId);
        guardedBody = { ...body, process_execution_id: nextProcessExecutionId };
      }
      nextRow = {
        construction_id: nextConstructionId,
        material_qr_id: body.material_qr_id !== undefined ? body.material_qr_id : existing.material_qr_id,
        stir_batch_id: nextStirBatchId,
        process_execution_id: nextProcessExecutionId,
        // 旧キャッシュ行(used_ratio 未保持)は全量消費(1.0)として扱う(Σ が NaN になり検証を素通りするのを防ぐ)。
        used_ratio: (body.used_ratio !== undefined ? body.used_ratio : existing.used_ratio) ?? 1,
        excludeConsumptionId: existing.id,
      };
      if (kind === "patch") {
        const parentChanged = nextConstructionId !== existing.construction_id || nextStirBatchId !== existing.stir_batch_id || nextProcessExecutionId !== existing.process_execution_id;
        checkParents = parentChanged;
      }
    }

    const constructions = await this.indexedDB.constructionDataApi.getAllByOwner(ownerId, false);
    const processExecutions = await this.indexedDB.generated.processExecution.getAllByOwner(ownerId, false);
    const message = validateConsumptionWrite(nextRow, { constructions, stirBatches, processExecutions, consumptions }, checkParents);
    if (message != null) {
      throw new Error(message);
    }
    return guardedBody;
  }

  // 工程実施/撹拌バッチのオフライン削除。親行の更新と、配下の消費行(施工材料消費)のコホート
  // カスケードを1トランザクションで原子的に書き込む(途中失敗による半端な削除状態を防ぐ)。
  private async offlineDeleteConsumptionParent(
    name: "stirBatches" | "processExecutions",
    foreignKeyField: "stir_batch_id" | "process_execution_id",
    id: string,
    deleted: boolean,
  ): Promise<StatusResponse> {
    const ownerId = this.currentUser.id;
    const parentApi = this.indexedDB.GetEntityApi(this.offlineStoreFor(name));
    const consumptionApi = this.indexedDB.generated.constructionMaterialConsumption;

    const parent = (await parentApi.getAllByOwner(ownerId, false)).find((row) => row.id === id);
    if (!parent) {
      throw new Error(`${parentApi.def.store}に更新対象のデータがありません。`);
    }
    const previousDeletedAt = parent.deleted_at ?? null;
    if (deleted && previousDeletedAt != null) {
      // 既に削除済みの親への再削除は no-op(コホートの削除時刻を保つ)。
      return { status: "ok" };
    }

    const now = Time.Now().GetTime();
    const deletedAt = deleted ? now : null;
    const parentRow = { ...parent, deleted_at: deletedAt, updatedAt: now };

    const targetConsumptions = (await consumptionApi.getAllByOwner(ownerId, false)).filter((consumption) => consumption[foreignKeyField] === id);
    const consumptionRows = buildConsumptionCohortRows(targetConsumptions, deleted, previousDeletedAt, deletedAt, now);

    await this.indexedDB.putRowsAtomic([
      { store: parentApi.def.store, rows: [parentRow] },
      { store: consumptionApi.def.store, rows: consumptionRows },
    ]);
    return { status: "ok" };
  }

  // entities.putRow/patchRow/setDelete共通の送信処理。
  // hasMerge:false(マスタ・区分子テーブル)はオンライン限定のまま維持し、失敗はそのまま例外にする。
  // hasMerge:true はオフライン時、および送信失敗時(fetch例外・5xx)にIndexedDBへフォールバックする。
  // 4xx等の業務エラーはフォールバックせず表示・例外化する(request()と同じ扱い)。
  private async writeEntityRow<T>(
    method: HttpMethod,
    path: OnlineRequestPath,
    body: unknown,
    resSchema: z.ZodType<T>,
    hasMerge: boolean,
    offlineFallback: () => Promise<T>,
  ): Promise<T> {
    if (!hasMerge) {
      const response = await AuthedApi.onlineRequest(z.any(), method, path, body);
      return await AuthedApi.GetResponseData(response, resSchema);
    }

    if (!this.isOnline) {
      return await offlineFallback();
    }

    let response: Response | null = null;
    try {
      response = await AuthedApi.onlineRequest(z.any(), method, path, body);
    } catch (e) {
      this.isOnline = false;
    }

    if (response == null) {
      return await offlineFallback();
    }

    if (response.ok && 200 <= response.status && response.status < 300) {
      return await AuthedApi.GetResponseData(response, resSchema);
    }

    if (500 <= response.status) {
      this.isOnline = false;
      return await offlineFallback();
    }

    const json = await response.json();
    if (json.code === ErrorCode.InvalidApiAuthToken || json.code === ErrorCode.ApiAuthTokenNotFound || json.code === ErrorCode.InvalidToken) {
      // request() と同じ扱い。認証切れはオンライン接続自体が生きていても続行できないため、
      // 現在の作業を失わないようオフラインへフォールバックする。
      this.showError(`認証エラー：${json.error}\n前回ログインから時間が経ち過ぎているか、ユーザーが削除されたためオンライン状態を維持できませんでした。\n現在の作業を保存するため一時的にオフラインモードに移行しています。\n作業終了後再度ログインしてください。`);
      this.isOnline = false;
      return await offlineFallback();
    }

    this.showError(`リクエストエラー\n${json.error}`);
    throw new ApiError(json.code, json.error);
  }

  // 生成エンティティ(マスタ/新規トランザクション)のキャッシュ読み出し&サーバー書き込み。
  // マスタ系は read 中心、トランザクション系は merge 中心。replaceCollection の引数として
  // 渡された場合に IDB と並行してサーバーへも書き込みを行う。
  public readonly entities = {
    // 指定エンティティのキャッシュから現在ユーザー所有・未削除の全行を取得する
    getAll: async <T extends { id: string | number; ownerId?: string; deleted_at?: number | null }>(
      name: keyof typeof idbEntityDefs
    ): Promise<T[]> => {
      const cache = await this.indexedDB.GetEntityApi(idbEntityDefs[name].store).getCache();
      const ownerId = this.currentUser.id;
      // ストア名からの動的引きでは行の静的型が EntityRowBounds まで落ちるため、呼び出し側が
      // 指定するエンティティ型へ unknown 経由で復元する(行は zod 検証済みの経路でのみ書かれる)。
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- ストア名の動的引きで行の静的型が落ちる境界。行は zod 検証済み経路でのみ書かれる
      return cache.filter((d) => d.ownerId === ownerId && d.deleted_at == null) as unknown as T[];
    },
    // 指定エンティティのキャッシュから1行を取得する(未削除のみ)
    getFromId: async <T extends { id: string | number; ownerId?: string; deleted_at?: number | null }>(
      name: keyof typeof idbEntityDefs,
      id: T["id"]
    ): Promise<T | null> => {
      const cache = await this.indexedDB.GetEntityApi(idbEntityDefs[name].store).getCache();
      const ownerId = this.currentUser.id;
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- 同上(動的ストア名引きの境界)
      const hit = cache.find((d) => d.id === id && d.ownerId === ownerId && d.deleted_at == null) as unknown as T | undefined;
      return hit ?? null;
    },

    // 新規行をサーバーに登録する。AreaDivision の場合 row.location_labels は同送される。
    // 戻り値は API レスポンス { status: "ok", id: ... }(idType により string/number)。
    // hasMerge:true(施工実施系トランザクション)はオフライン時/送信失敗時にIndexedDBへフォールバックする。
    // hasMerge:false(マスタ・区分子テーブル)はフォールバックせず従来どおりオンライン限定のまま。
    putRow: async (name: EntityWriteKey, row: Record<string, unknown>): Promise<{ status: string; id: string | number }> => {
      const info = SLUG_MAP[name];
      const path = `/put-${info.sing}` as const;
      // base から id/createdAt/updatedAt/deleted_at を取り除いた値を送る。
      // 余分なキー(ownerId 等)も含めて API 側 zod が strip するため neutral だが、
      // 通常運用ではここで余計なキーを落としておく。
      const { id, createdAt, updatedAt, deleted_at, ownerId, cachedAt, ...sanitized } = row ?? {};
      const body: Record<string, unknown> = sanitized;
      // location_labels は AreaDivision の put/patch でのみ受理される。
      // 他エンティティで誤って付与されていた場合に備え除去する。
      if (!("nestedLabels" in info)) {
        delete body.location_labels;
      }
      const resSchema = buildPutResSchema(info.idType);
      return await this.writeEntityRow<{ status: string; id: string | number }>("PUT", path, body, resSchema, info.hasMerge, async () => {
        let offlineBody = body;
        if (name === "constructionMaterialConsumptions") {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- 呼び出し元行の sanitize 済みスプレッドを消費行ペイロード形へ写す境界(内容は validateConsumptionWrite が検証)
          offlineBody = await this.prepareOfflineConsumptionWrite("put", body as ConsumptionWriteBody);
        }
        return await this.indexedDB.GetEntityApi(this.offlineStoreFor(name)).offlinePut({ ...offlineBody, ownerId: this.currentUser.id });
      });
    },

    // 指定エンティティをサーバから全件GETし、ローカルIDBキャッシュへ追従させる。
    // putRow/patchRow/setDelete(サーバ送信のみ)後に呼び、登録/変更をローカル一覧へ反映する。
    // canOverwriteByServerData により未送信のローカル編集(dirty)は保護される。
    getListAndCache: async (name: EntityWriteKey): Promise<void> => {
      const info = SLUG_MAP[name];
      const mergeDef = idbMergeDefs.find((d) => d.path === `/merge-${info.plural}`);
      if (mergeDef == null) {
        throw new Error(`getListAndCache: ${String(name)} はキャッシュ対象(merge対応)ではありません。`);
      }
      const rows = await this.getRowsFromServer(info.plural);
      // 全件スナップショットGETのため、一覧に無い既存キャッシュ行は削除済みとして トゥームストーン 化する(削除の伝播)。
      // 応答は未検証(unknown[])のため、サーバの /get-{plural} 応答契約(audit:drift が監視)を前提に行型として扱う。
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- 未検証のサーバ応答を行型へ写す境界(検証導入は挙動変更のため見送り)
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- 同上(未検証サーバ応答の境界)
      await this.indexedDB.GetEntityApi(mergeDef.store).cacheAllAndReconcileMissing(rows as EntityRowBounds[], { ownerId: this.currentUser.id }, Time.Now().GetTime());
    },

    // SLUG_MAP外(WithChildren方式)のエンティティを全件GET+IDBキャッシュする汎用経路。
    // 施工系一覧はローカルIDBを読むため、表示前に plural と merge パスを指定してサーバ値を取り込む。
    syncMergeEntity: async (plural: EntityGetPlural, mergePath: string): Promise<void> => {
      const mergeDef = idbMergeDefs.find((d) => d.path === mergePath);
      if (mergeDef == null) {
        return;
      }
      const rows = await this.getRowsFromServer(plural);
      // 全件スナップショットGETのため、一覧に無い既存キャッシュ行は削除済みとして トゥームストーン 化する(削除の伝播)。
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- 同上(未検証サーバ応答の境界)
      await this.indexedDB.GetEntityApi(mergeDef.store).cacheAllAndReconcileMissing(rows as EntityRowBounds[], { ownerId: this.currentUser.id }, Time.Now().GetTime());
    },

    // 施工系の一覧/詳細が依存する全トランザクション(施工/施工箇所+各子テーブル)を取り込む。
    // 各画面はローカルIDBを読むため、表示前にこれを呼んでサーバ値へ追従させる。各失敗は握りつぶす。
    syncConstructionData: async (): Promise<void> => {
      const cacheEntityRows = async (plural: EntityGetPlural, entityName: keyof typeof idbEntityDefs) => {
        const rows = await this.getRowsFromServer(plural);
        // 全件スナップショットGETのため、一覧に無い既存キャッシュ行は削除済みとして トゥームストーン 化する(削除の伝播)。
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- 同上(未検証サーバ応答の境界)
        await this.indexedDB.GetEntityApi(idbEntityDefs[entityName].store).cacheAllAndReconcileMissing(rows as EntityRowBounds[], { ownerId: this.currentUser.id }, Time.Now().GetTime());
      };
      try {
        await this.entities.syncMergeEntity("constructions", "/merge-constructions");
      } catch (e) {
        console.warn("sync constructions failed", e);
      }
      try {
        await cacheEntityRows("construction-records", "ConstructionRecord");
      } catch (e) {
        console.warn("sync construction-records failed", e);
      }
      try {
        await this.entities.syncMergeEntity("construction-plan-areas", "/merge-construction-plan-areas");
      } catch (e) {
        console.warn("sync construction-plan-areas failed", e);
      }
      try {
        await cacheEntityRows("construction-plan-area-locations", "ConstructionPlanAreaLocation");
      } catch (e) {
        console.warn("sync construction-plan-area-locations failed", e);
      }
      try {
        await cacheEntityRows("construction-plan-area-planned-materials", "ConstructionPlanAreaPlannedMaterial");
      } catch (e) {
        console.warn("sync construction-plan-area-planned-materials failed", e);
      }
      const childKeys: EntityWriteKey[] = ["processExecutions", "stirBatches", "constructionActions", "constructionActionAreas", "qualityRecords", "constructionMaterialConsumptions"];
      for (const key of childKeys) {
        try {
          await this.entities.getListAndCache(key);
        } catch (e) {
          console.warn(`getListAndCache ${String(key)} failed`, e);
        }
      }
    },

    // 既存行を更新する。AreaDivision の場合 row.location_labels も同送される。
    // hasMerge:true はオフライン時/送信失敗時にIndexedDBへフォールバックする。
    patchRow: async (name: EntityWriteKey, row: Record<string, unknown> & { id: string | number }): Promise<StatusResponse> => {
      const info = SLUG_MAP[name];
      if (row == null || row.id == null) {
        throw new Error(`patchRow: ${String(name)} には id が必須です。`);
      }
      const path = `/patch-${info.sing}` as const;
      const { createdAt, updatedAt, deleted_at, ownerId, cachedAt, ...sanitized } = row;
      const body: Record<string, unknown> & { id: string | number } = sanitized;
      if (!("nestedLabels" in info)) {
        delete body.location_labels;
      }
      return await this.writeEntityRow("PATCH", path, body, statusResponseSchema, info.hasMerge, async () => {
        let offlineBody: Record<string, unknown> = body;
        if (name === "constructionMaterialConsumptions") {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- 同上(消費行ペイロード形への境界)
          offlineBody = await this.prepareOfflineConsumptionWrite("patch", body as ConsumptionWriteBody);
        }
        return await this.indexedDB.GetEntityApi(this.offlineStoreFor(name)).offlinePatch({ ...offlineBody, id: body.id, ownerId: this.currentUser.id });
      });
    },

    // 論理削除/復活フラグを送信する(整数PK/UUID PK 共通フォーマット { id, deleted })。
    // hasMerge:true はオフライン時/送信失敗時にIndexedDBへフォールバックする。
    setDelete: async (name: EntityWriteKey, id: string | number, deleted: boolean = true): Promise<StatusResponse> => {
      const info = SLUG_MAP[name];
      const path = `/set-delete-${info.sing}` as const;
      const body: { id: string | number; deleted: boolean } = { id, deleted };
      return await this.writeEntityRow("DELETE", path, body, statusResponseSchema, info.hasMerge, async () => {
        if (name === "constructionMaterialConsumptions" && !deleted) {
          // 復元はAPIと同じく全親の生存とΣを検証する(削除方向は検証不要)。
          // 消費行の id は UUID(文字列)のため String() は恒等変換
          await this.prepareOfflineConsumptionWrite("restore", { id: String(body.id) });
        }
        return await this.indexedDB.GetEntityApi(this.offlineStoreFor(name)).offlineSetDelete({ id, ownerId: this.currentUser.id, deleted });
      });
    },

    // 工程実施/撹拌バッチの削除。配下の消費行(施工材料消費)も同じコホートでカスケードする。
    // 親だけ削除すると消費行が座礁し材料が消費済みのまま残るため、オンラインは API の set-delete-… が
    // サーバ側で、オフラインは offlineDeleteConsumptionParent がローカルIDBで消費行を同時に論理削除/復元する。
    setDeleteWithConsumptionCascade: async (name: "stirBatches" | "processExecutions", id: string, deleted: boolean = true): Promise<StatusResponse> => {
      const info = SLUG_MAP[name];
      const foreignKeyField = name === "stirBatches" ? "stir_batch_id" : "process_execution_id";
      const path = `/set-delete-${info.sing}` as const;
      const body: { id: string; deleted: boolean } = { id, deleted };
      return await this.writeEntityRow("DELETE", path, body, statusResponseSchema, info.hasMerge, () =>
        this.offlineDeleteConsumptionParent(name, foreignKeyField, id, deleted)
      );
    },

    // UUID キーの新規トランザクション系のみ。整数マスタには無いエンドポイント。
    merge: async (name: EntityWriteKey, rows: ReadonlyArray<Record<string, unknown>>): Promise<StatusResponse> => {
      const info = SLUG_MAP[name];
      if (!info.hasMerge) {
        throw new Error(`merge: ${String(name)} はマージ非対応エンティティです。`);
      }
      const path = `/merge-${info.plural}` as const;
      const response = await AuthedApi.onlineRequest(z.unknown(), "POST", path, rows);
      return await AuthedApi.GetResponseData(response, statusResponseSchema);
    },
  };

  // 取り込みがエラーで返ったときに、サーバーが載せたエラーコードを保ったまま例外にする。
  // 呼び出し側が文言の解析ではなくコードで種類を見分けられるようにするため(request と同じ作法)。
  // コードや説明文が読み取れない応答は、内部エラーとして扱う。
  private static async throwImportError(response: Response): Promise<never> {
    const json: unknown = await response.json().catch(() => ({}));
    const body = readErrorBody(json);
    throw new ApiError(body.code, body.message);
  }

  // マスターCSVの一括取り込みをサーバーへ依頼する(オンライン限定)。
  // 参照先の実在確認・ID衝突の判定・再採番・自然キーの連番付与はすべてサーバーが1トランザクションで行う。
  // 要求の mode が "dryRun" のときは同じ処理を最後まで行ってから巻き戻すため、結果だけを受け取れる。
  public async importMasters(request: MasterImportRequest): Promise<MasterImportResponse> {
    const response = await AuthedApi.onlineRequest(masterImportRequestSchema, "POST", "/import-masters", request);
    if (!response.ok) {
      return await AuthedApi.throwImportError(response);
    }
    return await AuthedApi.GetResponseData(response, masterImportResponseSchema);
  }

  // マスタ等をサーバから取得する(オンライン限定)。応答は行配列(bare array)、または
  // 正規化応答(トップ複数形キー + related バケット。工事情報/エリア区分/場所区分の一覧が該当)。
  // 正規化応答はトップ行配列へ展開して返すため、呼び出し側の契約は従来どおり「行配列」のまま。
  // related の子行(技能者・子ラベル・親経路)はここでは使わない: 各子エンティティ自身が
  // 整数PKマスタとして /get-{plural} 同期経路(dbSlice.MASTER_SYNC_KEYS)を持つため。
  // キャッシュ保存/更新は呼び出し側(dbSlice.syncMastersFromServer)が cacheAll で行う(他データ同様)。
  public async getRowsFromServer(plural: EntityGetPlural): Promise<unknown[]> {
    const path: EntityGetPath = `/get-${plural}`;
    const response = await AuthedApi.onlineRequest(z.object({}), "GET", path, {});
    if (!(response.ok && 200 <= response.status && response.status < 300)) {
      throw new ApiError(response.status, `get-${plural} の取得に失敗しました。`);
    }
    const data = await AuthedApi.GetResponseData(response, z.union([z.array(z.unknown()), normalizedRowsEnvelopeSchema]));
    if (Array.isArray(data)) {
      return data;
    }
    return topRowsOfNormalized(data, plural);
  }
}

// サーバーがエラー時に返す本文。code は ErrorCode、error は利用者向けの説明文。
// 通信断などで JSON にならなかった場合に備えて、読み取れた分だけを取り出す。
function readErrorBody(json: unknown): { code: number; message: string } {
  const errorBodySchema = z.object({ code: z.number(), error: z.string() }).partial();
  const parsed = errorBodySchema.safeParse(json);
  if (parsed.success) {
    return { code: parsed.data.code ?? ErrorCode.InternalError, message: parsed.data.error ?? "マスタの取り込みに失敗しました。" };
  }
  return { code: ErrorCode.InternalError, message: "マスタの取り込みに失敗しました。" };
}

// 正規化応答の受信形。related 以外のキー(トップ複数形キー)はすべて行配列。
const normalizedRowsEnvelopeSchema = z.object({ related: z.record(z.array(z.unknown())) }).catchall(z.array(z.unknown()));

// 正規化応答からトップ行配列を取り出す。トップキーは related 以外の唯一のキー(単一トップエンティティの契約)。
function topRowsOfNormalized(data: z.infer<typeof normalizedRowsEnvelopeSchema>, plural: EntityGetPlural): unknown[] {
  const topKeys = Object.keys(data).filter((key) => key !== "related");
  const topKey = topKeys[0];
  if (topKeys.length !== 1 || topKey === undefined) {
    throw new Error(`get-${plural} の正規化応答からトップ行配列を特定できません(キー: ${topKeys.join(", ")})`);
  }
  const rows = data[topKey];
  if (rows === undefined) {
    throw new Error(`get-${plural} の正規化応答のトップキー ${topKey} が行配列ではありません`);
  }
  return rows;
}
